import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PhoneCall, X, StickyNote, History } from "lucide-react";
import { STAGE_LABEL, api } from "@/lib/api";

const chime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [660, 990].forEach((f, i) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.frequency.value = f; o.connect(g); g.connect(ctx.destination);
      const t = ctx.currentTime + i * 0.18;
      g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.25, t + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
      o.start(t); o.stop(t + 0.32);
    });
  } catch { /* audio blocked until user interacts */ }
};

const left = (a) => Math.max(0, Math.ceil((new Date(a.dial_at).getTime() - Date.now()) / 1000));
const when = (v) => (v ? new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

const Row = ({ icon: Icon, label, children, testid }) => (
  <div className="flex gap-2.5" data-testid={testid}>
    <Icon className="h-4 w-4 mt-0.5 shrink-0 text-clay" />
    <div className="min-w-0"><div className="label-caps text-forest/50">{label}</div><div className="text-sm text-forest whitespace-pre-line break-words line-clamp-4">{children}</div></div>
  </div>
);

export const PreCallAlert = () => {
  const [alert, setAlert] = useState(null);
  const [secs, setSecs] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    const seen = new Set();
    const show = (a) => { if (seen.has(a.id) || left(a) <= 0) return; seen.add(a.id); chime(); setAlert(a); };
    const on = (e) => show(e.detail);
    const poll = () => { if (!document.hidden) api.get("/live/precall").then((r) => r.data.items.forEach(show)).catch(() => {}); };
    poll();
    const t = setInterval(poll, 2000);
    window.addEventListener("propzel:precall", on);
    return () => { clearInterval(t); window.removeEventListener("propzel:precall", on); };
  }, []);

  useEffect(() => {
    if (!alert) return undefined;
    const tick = () => { const s = left(alert); setSecs(s); if (s <= 0) setTimeout(() => setAlert((c) => (c?.id === alert.id ? null : c)), 4000); };
    tick();
    const t = setInterval(tick, 250);
    return () => clearInterval(t);
  }, [alert]);

  if (!alert) return null;
  const { lead = {}, last_followup: lf, last_note: ln } = alert;
  const notes = [alert.followup_notes, lead.notes].filter(Boolean).join("\n");
  const pct = Math.min(100, (secs / 30) * 100);
  return (
    <div data-testid="precall-alert" role="button" tabIndex={0} onClick={() => { nav(`/leads/${alert.lead_id}`); }} onKeyDown={(e) => e.key === "Enter" && nav(`/leads/${alert.lead_id}`)} className="cursor-pointer hover:border-forest fixed z-[60] right-4 left-4 sm:left-auto bottom-4 sm:w-[380px] bg-white border border-[#E6E4DD] shadow-2xl rounded-sm overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-300">
      <div className="h-1 bg-[#E6E4DD]"><div className="h-full bg-clay transition-[width] duration-200 ease-linear" style={{ width: `${pct}%` }} /></div>
      <div className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-forest text-white grid place-items-center shrink-0 animate-pulse"><PhoneCall className="h-5 w-5" /></div>
          <div className="min-w-0 flex-1">
            <div className="label-caps text-forest/50">{secs > 0 ? "Auto-dial coming up" : "Dialling now"}</div>
            <div data-testid="precall-lead-name" className="font-display font-bold text-lg text-forest leading-tight truncate">
              Calling {lead.name || "lead"} next
            </div>
            <div className="text-xs text-forest/60 truncate">{[lead.phone, lead.project_name].filter(Boolean).join(" · ")}</div>
          </div>
          <div className="text-right shrink-0">
            <div data-testid="precall-countdown" className="font-display font-bold text-2xl text-clay tabular-nums leading-none">{secs}s</div>
            <button data-testid="precall-close-btn" onClick={(e) => { e.stopPropagation(); setAlert(null); }} aria-label="Close" className="mt-2 h-7 w-7 inline-grid place-items-center rounded-sm text-forest/60 hover:bg-[#F3F1EA] hover:text-forest transition-colors duration-150"><X className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="space-y-2.5 border-t border-[#E6E4DD] pt-3">
          {lead.stage && <div data-testid="precall-status" className="inline-flex text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-sm bg-[#F3F1EA] text-forest">Status · {STAGE_LABEL[lead.stage] || lead.stage}</div>}
          <Row icon={StickyNote} label="Lead notes" testid="precall-notes">{notes || ln?.message || "No notes yet"}</Row>
          {lf && <Row icon={History} label={`Last follow-up · ${lf.status} · ${when(lf.due_at)}`} testid="precall-last-followup">{lf.notes || "No note added"}</Row>}
          {ln && notes && <Row icon={History} label={`Last note · ${when(ln.created_at)}`} testid="precall-last-note">{ln.message}</Row>}
        </div>
        <div className="text-[11px] text-forest/50">Your phone rings when the timer ends. Closing this only hides it.</div>
      </div>
    </div>
  );
};
